package top.wcpe.allincore.ui.runtime.theme

import kotlin.jvm.JvmStatic

/**
 * 主题 token 查询表：IR 绑定的不可变值对象。
 *
 * 每次 `IrInterpreter` 解释一份新的 `UiIr` 时，应基于 `UiIr.getThemeTokens()` 构造一个新的
 * ThemeLookup 注入到 `EvalContext`，**不要**当成全局单例使用。
 *
 * 值类型限定为 String / Double / Boolean，与 `UiIr.themeTokens` 的约束保持一致；本类不做二次校验，
 * 相信上游已校验。
 */
class ThemeLookup private constructor(private val tokens: Map<String, Any?>) {

    /** 命中返回 String/Double/Boolean，未命中返回 null。 */
    fun get(key: String): Any? = tokens[key]

    fun contains(key: String): Boolean = tokens.containsKey(key)

    /**
     * 取颜色 token：支持字符串字面量（#RRGGBB / #AARRGGBB）与数值（Long/Double，按 ARGB int 解释）。
     * 无效 / 缺失时返回 [fallback]。
     */
    fun getColor(key: String, fallback: Int): Int = when (val v = tokens[key]) {
        is Number -> v.toInt()
        is String -> parseColor(v, fallback)
        else -> fallback
    }

    /**
     * 取颜色 token：合法时返回 ARGB int；key 缺失或值非法（非 Number/String、畸形 hex）返回 null。
     *
     * 与 [getColor] 的区别：不返回 fallback，把「未声明 / 非法」与「显式颜色」区分开，
     * 供需要感知「未声明」的调用方（如 typography 样式解析）使用。
     */
    fun getColorOrNull(key: String): Int? = when (val v = tokens[key]) {
        is Number -> v.toInt()
        is String -> parseColorOrNull(v)
        else -> null
    }

    /**
     * 取数值 token：Number 直接 toDouble；其余类型返回 [fallback]。
     * 主要用于 stateLayer*Opacity 等浮点字段。
     */
    fun getNumber(key: String, fallback: Double): Double {
        val v = tokens[key]
        return if (v is Number) v.toDouble() else fallback
    }

    /** 取字符串 token：String 直接返回；其它类型 / 缺失返回 [fallback]。 */
    fun getString(key: String, fallback: String?): String? {
        val v = tokens[key]
        return if (v is String) v else fallback
    }

    /**
     * 收集所有以 prefix + "." 开头的 key 的剥前缀子集合，按 dotted key 还原为 nested 视图。
     *
     * 示例：tokens 含 `toastStyle.warning.width`、`toastStyle.warning.backgroundColor`、
     * `toastStyle.success.width`，调用 `childKeys("toastStyle")` 返回 `["warning", "success"]`
     * （保留首次出现顺序）。用于把扁平 dotted key 还原为嵌套视图。
     */
    fun childKeys(prefix: String?): Set<String> {
        if (prefix.isNullOrEmpty()) return emptySet()
        val dotted = "$prefix."
        val result = LinkedHashSet<String>()
        for (key in tokens.keys) {
            if (!key.startsWith(dotted)) continue
            val next = key.indexOf('.', dotted.length)
            val child = if (next < 0) key.substring(dotted.length) else key.substring(dotted.length, next)
            if (child.isNotEmpty()) result.add(child)
        }
        return result
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is ThemeLookup) return false
        return tokens == other.tokens
    }

    override fun hashCode(): Int = tokens.hashCode()

    override fun toString(): String = "ThemeLookup(keys=${tokens.keys})"

    companion object {
        /** 十六进制基数。 */
        private const val HEX_RADIX = 16

        /** 不含 alpha 的 RRGGBB 字面量长度。 */
        private const val HEX_RGB_LENGTH = 6

        /** 含 alpha 的 AARRGGBB 字面量长度。 */
        private const val HEX_ARGB_LENGTH = 8

        /** RRGGBB 缺省补足的不透明 alpha 掩码。 */
        private const val OPAQUE_ALPHA_MASK = 0xFF000000L

        /** 共享空实例：不可变，可安全复用。 */
        private val EMPTY = ThemeLookup(emptyMap())

        /** 返回共享的空 lookup。 */
        @JvmStatic
        fun empty(): ThemeLookup = EMPTY

        /** 基于 token 表构造 lookup（防御式拷贝，保留插入顺序）。 */
        @JvmStatic
        fun of(tokens: Map<String, Any?>?): ThemeLookup {
            if (tokens == null || tokens.isEmpty()) return EMPTY
            return ThemeLookup(LinkedHashMap(tokens))
        }

        /**
         * 解析 #RRGGBB / #AARRGGBB（# 可省）十六进制颜色；非法（非 hex 字符 / 长度非 6/8）返回 [fallback]。
         *
         * 与 [parseColorOrNull] 复用同一套长度校验，避免「短 hex 在 getColor 下被当作合法色」的规则分裂。
         */
        private fun parseColor(raw: String, fallback: Int): Int = parseColorOrNull(raw) ?: fallback

        /** 解析 #RRGGBB / #AARRGGBB（# 可省）十六进制颜色；非法（非 hex 字符 / 长度不对）返回 null。 */
        private fun parseColorOrNull(raw: String): Int? {
            val s = raw.trim().removePrefix("#")
            if (s.isEmpty() || s.length != HEX_RGB_LENGTH && s.length != HEX_ARGB_LENGTH) return null
            val parsed = s.toLongOrNull(HEX_RADIX) ?: return null
            return if (s.length == HEX_RGB_LENGTH) {
                (OPAQUE_ALPHA_MASK or parsed).toInt()
            } else {
                parsed.toInt()
            }
        }
    }
}
